import { motion } from "framer-motion";
import { Camera, Activity, Utensils, Sparkles } from "lucide-react";

/**
 * Body Transformation flagship section — Mobile Tech Agents.
 *
 * Renders ONLY on `/programs/body-transformation`. Highlights the cell-phone
 * tech agents that automate food, exercise, and physiology logging — removing
 * the friction of manual tracking and feeding directly into the IWCE pipeline.
 *
 * No "Patent Pending" badge per current scoping — marketed as production
 * tech, not legally claimed yet.
 */

const AGENTS = [
  {
    icon: Utensils,
    label: "Food Agent",
    title: "Snap. Logged.",
    body:
      "Point your camera at the plate. The Food Agent identifies portions, macros, and micronutrients — no manual entry, no forgotten meals. Every meal lands in your IWCE record automatically.",
  },
  {
    icon: Activity,
    label: "Movement Agent",
    title: "Every rep counts itself.",
    body:
      "Strength sessions, cardio, mobility — auto-logged from your wearable, plus AI-assisted form review from a quick clip. The Movement Agent turns workouts into structured training data.",
  },
  {
    icon: Camera,
    label: "Physiology Agent",
    title: "Daily body read.",
    body:
      "A 60-second cell-phone scan reads vascular reactivity, recovery state, and biometric drift. The Physiology Agent surfaces resilience signals long before standard labs would.",
  },
];

const VIEWPORT_ONCE = { once: true };
const REVEAL_UP = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};
const CARD_REVEAL = {
  hidden: { opacity: 0, y: 18 },
  visible: (index) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: index * 0.08 } }),
};
const FADE_REVEAL = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6, delay: 0.3 } },
};
const LATE_REVEAL = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.4 } },
};

export default function BodyTransformationMobileTech() {
  return (
    <section
      data-testid="body-transformation-mobile-tech"
      className="max-w-6xl mx-auto px-4 md:px-8 mt-20 md:mt-28"
    >
      <motion.div
        variants={REVEAL_UP}
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT_ONCE}
      >
        <div className="flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.32em] text-accent mb-4">
          <Sparkles size={14} aria-hidden />
          Flagship · Mobile Tech Agents
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-4xl">
          Body transformation with{" "}
          <em className="not-italic text-primary">zero manual logging</em>.
        </h2>
        <p className="mt-6 text-lg md:text-xl text-foreground/80 leading-relaxed max-w-3xl">
          The hardest part of any body-transformation program isn&rsquo;t the training — it&rsquo;s
          the tracking. Our cell-phone tech agents automate food, exercise, and physiology logging
          end-to-end, feeding clean data directly into the IWCE pipeline so your protocol can
          actually adapt in real time.
        </p>
      </motion.div>

      <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
        {AGENTS.map(({ icon: Icon, label, title, body }, i) => (
          <motion.div
            key={label}
            custom={i}
            variants={CARD_REVEAL}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE}
            data-testid={`mobile-tech-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="premium-card p-7 md:p-8 bg-card border-2 border-border rounded-2xl hover:border-primary/45 transition-colors"
          >
            <Icon size={28} className="text-primary mb-5" aria-hidden />
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-accent mb-2">
              {label}
            </div>
            <h3 className="font-display text-2xl font-semibold leading-tight">{title}</h3>
            <p className="mt-3 text-base text-foreground/75 leading-relaxed">{body}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        variants={FADE_REVEAL}
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT_ONCE}
        className="mt-10 md:mt-12 p-7 md:p-9 rounded-2xl bg-foreground text-background border border-foreground"
      >
        <div
          className="font-mono text-[0.6rem] uppercase tracking-[0.22em] mb-3"
          style={{ color: "hsl(38 38% 88% / 0.7)" }}
        >
          Why It Matters
        </div>
        <p
          className="font-display text-xl md:text-2xl leading-snug max-w-3xl"
          style={{ color: "hsl(38 38% 95%)" }}
        >
          Manual logging fails within 6 weeks for ~85% of users. The Mobile Tech Agents remove
          the friction — so your protocol actually has the data it needs to adapt and titrate
          alongside your clinical team.
        </p>
      </motion.div>

      {/* Zero-Friction Ingestion — patent-covered method */}
      <motion.div
        variants={LATE_REVEAL}
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT_ONCE}
        className="mt-6 p-7 md:p-9 rounded-2xl bg-card border-2 border-accent/40"
      >
        <div className="flex items-center gap-2 mb-3">
          <span
            data-testid="zero-friction-badge"
            className="font-mono text-[0.6rem] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-accent text-accent-foreground"
          >
            Now Patent-Covered
          </span>
          <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-accent">
            Zero-Friction Ingestion
          </div>
        </div>
        <h3 className="font-display text-xl md:text-2xl font-semibold leading-snug max-w-3xl">
          More than logging — the patent covers how raw mobile signals become clinical data.
        </h3>
        <p className="mt-4 text-base md:text-lg text-foreground/80 leading-relaxed max-w-3xl">
          The v3 patent covers the method of turning everyday mobile inputs into clinical data
          automatically — a <strong className="text-foreground">photo of your meal</strong>,{" "}
          <strong className="text-foreground">activity from your wearable</strong>, and a{" "}
          <strong className="text-foreground">cell-phone physiology scan</strong> — translated
          directly into the harmonic coefficients the clinical engine can act on. No manual
          logging. No middle-step data entry. Just signal in, protocol out.
        </p>
      </motion.div>
    </section>
  );
}
